import { SortingFn, SortingState, sortingFns } from '@tanstack/react-table'
import { useState } from 'react'
import { GridColDefPro, GridValueGetterParams } from '../utils/types'

/** Linhas que o TanStack pula antes de amostrar o valor da coluna. */
const AMOSTRA_INICIAL = 10

interface Ordenacao {
    sort: SortingState[number]
    coluna: GridColDefPro
}

/** O `sortingFn` do TanStack só lê o valor da célula, não a linha inteira. */
function linhaDoComparador(valor: unknown) {
    return { getValue: () => valor } as any
}

/**
 * Replica o `getAutoSortingFn` do grid: texto com dígito usa `alphanumeric`
 * (põe `80:00` antes de `100:00`), texto puro usa `text`, o resto `basic`.
 * Sem isso, planilha e PDF sairiam em ordem diferente da tela.
 *
 * O `slice` espelha o do TanStack 8.21.2 (`RowSorting.js:41`), que pula — não
 * limita — as dez primeiras linhas.
 */
function comparadorDaColuna(valores: unknown[]): SortingFn<any> {
    let temTexto = false
    for (const valor of valores.slice(AMOSTRA_INICIAL)) {
        if (valor instanceof Date) return sortingFns.datetime
        if (typeof valor !== 'string') continue
        temTexto = true
        if (/[0-9]/.test(valor)) return sortingFns.alphanumeric
    }

    return temTexto ? sortingFns.text : sortingFns.basic
}

/**
 * Ignora `hide` de propósito: quem imprime passa as colunas estáticas do
 * relatório, sem a visibilidade escolhida pelo usuário.
 */
function colunaOrdenavel(coluna?: GridColDefPro): coluna is GridColDefPro {
    return !!coluna && coluna.sortable !== false
}

function valorDaCelula(coluna: GridColDefPro, linha: any) {
    const value = linha[coluna.field]
    if (!coluna.valueGetter) return value
    const params: GridValueGetterParams = {
        row: linha,
        value,
        id: linha.id,
        field: coluna.field,
    }
    return coluna.valueGetter(params)
}

function ordenacoesAplicaveis(
    sortModel: SortingState,
    columns: GridColDefPro[],
): Ordenacao[] {
    return sortModel.flatMap(sort => {
        const coluna = columns.find(col => col.field === sort.id)
        return colunaOrdenavel(coluna) ? [{ sort, coluna }] : []
    })
}

/**
 * Aplica a ordenação do grid fora dele — export e impressão recebem as linhas
 * filtradas, mas na ordem da API.
 */
export function ordenarLinhas<T = any>(
    data: T[],
    sortModel: SortingState,
    columns: GridColDefPro[],
    /** Árvore achatada, quando as linhas ordenadas são só um nível dela. */
    amostra?: any[],
) {
    const ordenacoes = ordenacoesAplicaveis(sortModel, columns).map(
        ({ sort, coluna }) => ({
            sort,
            coluna,
            comparar: comparadorDaColuna(
                (amostra ?? data).map(linha => valorDaCelula(coluna, linha)),
            ),
        }),
    )

    if (!ordenacoes.length) return data

    const sorted = structuredClone(data)

    sorted.sort((a: any, b: any) => {
        for (const { sort, coluna, comparar } of ordenacoes) {
            const comparison = comparar(
                linhaDoComparador(valorDaCelula(coluna, a)),
                linhaDoComparador(valorDaCelula(coluna, b)),
                coluna.field,
            )

            if (comparison !== 0) return sort.desc ? -comparison : comparison
        }
        return 0
    })

    return sorted
}

export default function useDefaultSortModel(columns: Array<GridColDefPro>) {
    const [sortModel, setSortModel] = useState<SortingState>([])

    const sort = <T = any>(data?: T[]) =>
        !data?.length ? ([] as T[]) : ordenarLinhas(data, sortModel, columns)

    return [sortModel, setSortModel, sort] as const
}
